<svelte:window
    on:mousemove={pointerMove}
    on:mouseup={dragEnd}
    on:touchend={dragEnd}
    on:touchmove={pointerMove}
/>
<MoveResourceToAnotherPlanModal
    {showMoveToModal}
    {elementMoveTo}
    sourcePlanId={plan.id}
    {sourcePlanTopicId}
    {config}
    {i18n}
    on:elementMoveTo={e => elementMoved(e.detail)}
/>
{#if filteredElements.length}
    <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
    <ul
        tabindex={keyboardReorder ? "-1" : undefined}
        aria-labelledby={labelledBy}
        bind:this={listElement}
        class="plan-page__element-list"
        role={keyboardReorder ? "listbox" : undefined}
        aria-activedescendant={keyboardReorderActiveId
            ? "resource-node-{keyboardReorderActiveId}"
            : undefined}
        on:keydown={listKeyDown}
        on:blur={listBlur}
    >
        {#each filteredElements as element (element.id)}
            <ResourceNode
                resource={element}
                hidden={draggingElement && element.id === draggingElement.id}
                on:removeElementFromPlan={() => removeElementPlan(element)}
                on:removeElementFromCourse={() => removeElementCourse(element.id)}
                on:toggleElementActivation={e => toggleElementActivation(e.detail)}
                on:showMoveToModal={e =>
                    showMoveToModalChanged(e.detail, element, plan.topic ? plan.topic.id : null)}
                on:dragStart={e => dragStart(e.detail, element)}
                on:keyboardDragStart={() => keyboardDragStart(element)}
                reorderActive={keyboardReorder && keyboardReorderActiveId === element.id}
                {keyboardReorder}
                {isParent}
                {rowId}
                {elementsUrlExtraParams}
                planResources={plan.resources}
                {numberOfPlansInCourse}
            />
        {/each}
    </ul>
{/if}

{#if draggingElement}
    <div class="plan-page__element-dragging" style={draggedElementStyle}>
        <ResourceNode resource={draggingElement} size="large" {rowId} />
    </div>
{/if}

{#if showRemoveResourceModal}
    <Modal
        bind:enabled={showRemoveResourceModal}
        titleText={showRemoveResourceModalTitleText}
    >
        <div slot="body">
            {i18n.removeElementPlanTerms.removeElementModalBody}
        </div>
        <div slot="footer">
            <Button
                htmlType="button"
                large
                text={i18n.removeElementPlanTerms.removeElementModalButtonConfirmation}
                type="primary"
                on:click={removeSelectedElementFromPlan}
            />
            <Button
                htmlType="button"
                large
                text={i18n.cancel}
                type="secondary"
                on:click={() => showRemoveResourceModal = false}
            />
        </div>
    </Modal>
{/if}

<script>
    import { onMount, createEventDispatcher } from "svelte";

    import ResourceNode from "@its-ui/planner-common/components/ResourceNode/ResourceNode.svelte";
    import MoveResourceToAnotherPlanModal from "@its-ui/planner-common/components/MoveResourceToAnotherPlanModal/MoveResourceToAnotherPlanModal.svelte";
    import Button from "@itslearning/prometheus/assets/inputs/Button/v1/Button.svelte";
    import Modal from "@itslearning/prometheus/assets/modals/Modal/v2/Modal.svelte";
    import {
        deleteResourceFromPlan,
        activateResourceFromPlan,
        deleteResourceFromCourse
    } from "@its-ui/planner-common/controllers/updatePlanController";
    import { getFeatureStatus, setFeature  } from "@its-ui/planner-common/api";

    import { setElementOrder } from "@its-ui/planner-common/api";
    import { debounce } from "@itslearning/atlas/dom/Ratecontrol";
    import { normalizeKey } from "@itslearning/atlas/keyboard/normalize";
    import { Keys } from "@itslearning/atlas/keyboard/keys";
    import i18n from "@its-ui/planner-common/i18n";
    import config from "@its-ui/planner-common/config";
    import { noTopicTopicId } from "@its-ui/planner-common/constants.js";

    export let labelledBy;
    export let plan;
    export let field;
    export let elements;
    export let elementsFilter = () => true;
    export let orderDirty = false;
    export let isParent;
    export let rowId;
    export let customDeleteElementAction;
    export let customActivateResourceFromPlan;
    export let elementsUrlExtraParams;
    export let numberOfPlansInCourse;

    let showMoveToModal;
    let showRemoveResourceModal = false;
    let showRemoveResourceModalTitleText = "";
    let selectedElementId;
    let elementMoveTo;
    let sourcePlanTopicId;
    let keyboardReorder = false;
    let keyboardReorderActiveId;
    let previousFocusId;
    let dragDetails;
    let draggingElement = undefined;
    let debounceSaveElementOrder;
    let listElement;

    const dispatch = createEventDispatcher();

    $: filteredElements = elements.filter(elementsFilter);

    $: draggedElementStyle = draggingElement
        ? `top: ${dragDetails.y}px; left: ${dragDetails.x}px; width: ${dragDetails.width}px;`
        : "";

    onMount(() => {
        // Debounce saves to the element order to limit requests and to avoid race conditions
        // when two changes are made in quick succession.
        const runDebounceSaveElementOrder = debounce(saveElementOrder, 1000);

        debounceSaveElementOrder = () => {
            orderDirty = true;

            runDebounceSaveElementOrder();
        };
    });

    // Moves the element down the list relative to its position in the filtered elements
    function moveDown(element, filteredElementsIndex) {
        if (filteredElementsIndex >= filteredElements.length - 1) {
            return;
        }

        const target = filteredElements[filteredElementsIndex + 1];

        moveElement(element, target, false);

        debounceSaveElementOrder();
    }

    // Moves the element up the list relative to its position in the filtered elements
    function moveUp(element, filteredElementsIndex) {
        if (filteredElementsIndex <= 0) {
            return;
        }

        const target = filteredElements[filteredElementsIndex - 1];

        moveElement(element, target, true);

        debounceSaveElementOrder();
    }

    // Moves one element to a target element in the elements array,
    // inserting it either immediately before or after depending on insertBefore
    function moveElement(element, target, insertBefore) {
        const newElements = [...elements];
        const fromIndex = newElements.findIndex(e => e.id === element.id);

        newElements.splice(fromIndex, 1);

        const toIndex = newElements.findIndex(e => e.id === target.id) + (insertBefore ? 0 : 1);

        newElements.splice(toIndex, 0, element);

        elements = newElements;
    }

    function keyboardDragStart(element) {
        previousFocusId = document.activeElement.id;

        keyboardReorder = true;
        keyboardReorderActiveId = element.id;

        setTimeout(() => listElement.focus(), 0);
    }

    function listKeyDown(event) {
        if (!keyboardReorder) {
            return;
        }

        const filteredElementsIndex = filteredElements.findIndex(
            e => e.id === keyboardReorderActiveId
        );
        const activeElement = filteredElements[filteredElementsIndex];

        switch (normalizeKey(event.key)) {
            case Keys.UP:
                moveUp(activeElement, filteredElementsIndex);
                event.preventDefault();
                break;
            case Keys.DOWN:
                moveDown(activeElement, filteredElementsIndex);
                event.preventDefault();
                break;
            case Keys.ESCAPE:
            case Keys.SPACE:
            case Keys.TAB:
                event.stopPropagation();
                event.preventDefault();

                keyboardReorder = false;
                keyboardReorderActiveId = undefined;

                if (previousFocusId) {
                    document.getElementById(previousFocusId).focus();
                }

                break;
        }
    }

    function listBlur() {
        keyboardReorder = false;
        keyboardReorderActiveId = undefined;
    }

    // When dragging starts on an element get it's starting dimensions so it can be displayed
    // out of the list with absolute positioning
    function dragStart(event, element) {
        const rowRect = event.target.parentElement.getBoundingClientRect();

        dragDetails = {
            x: event.touches ? event.touches[0].pageX : event.pageX,
            y: event.touches ? event.touches[0].pageY : event.pageY,
            width: rowRect.width
        };

        draggingElement = element;
    }

    // While dragging an element set its y position to the cursor's y pos and check for
    // other elements under the pointer to re-order the list
    function pointerMove(event) {
        if (draggingElement) {
            dragDetails.y = event.touches ? event.touches[0].pageY : event.pageY;
            event.preventDefault();
            event.stopPropagation();

            let elementRect;
            let elementTop;
            let dragOverElement;

            // Iterate over <li>s in the list and check if the pointer is within any of them
            for (let i = 0; i < listElement.children.length; i++) {
                elementRect = listElement.children[i].getBoundingClientRect();
                elementTop = elementRect.y + document.body.parentElement.scrollTop;

                if (
                    dragDetails.y > elementRect.y &&
                    dragDetails.y < elementTop + elementRect.height
                ) {
                    dragOverElement = filteredElements[i];

                    break;
                }
            }

            // If an element (other than the dragged one) is hovered re-order the list to place the
            // dragged element above or below the dragOverElement depending on pointer position.
            if (dragOverElement && dragOverElement.id !== draggingElement.id) {
                const insertBefore = dragDetails.y - elementTop < elementRect.height / 2;

                moveElement(draggingElement, dragOverElement, insertBefore);
            }
        }
    }

    // The list is reordered while dragging so once it is released trigger a save and stop
    // dragging the element
    function dragEnd() {
        if (draggingElement) {
            debounceSaveElementOrder();

            draggingElement = null;
        }
    }

    // Posts the new element order to the API
    async function saveElementOrder() {
        orderDirty = true;

        await setElementOrder(
            plan.id,
            field.columnId,
            filteredElements[0].id,
            filteredElements.map(e => e.id)
        );

        orderDirty = false;
    }

    async function removeElementPlan(element) {
        if (plan.id && element.id) {
            selectedElementId = element.id;

            if(config.assignToEnabled)
            {
                const removeResourceFromPlanModal = 50;
                const isfeatureAlreadyCompleted = await getFeatureStatus(removeResourceFromPlanModal);

                if(isfeatureAlreadyCompleted && !isfeatureAlreadyCompleted.isCompleted)
                {
                    showRemoveResourceModal = true;
                    showRemoveResourceModalTitleText = i18n.removeElementPlanTerms.removeElementModalTitle.replace("{0}", element.title);
                    await setFeature(removeResourceFromPlanModal);
                }
                else
                {
                    await removeSelectedElementFromPlan();
                }

            } else {
                await removeSelectedElementFromPlan();
            }
        }
    }

    async function removeSelectedElementFromPlan()
    {
        await deleteResourceFromPlan(plan.id, selectedElementId);
        elements = elements.filter(e => e.id !== selectedElementId);
        showRemoveResourceModal = false;
    }

    async function removeElementCourse(elementId) {
        if (plan.id && elementId) {
            if (customDeleteElementAction) {
                await customDeleteElementAction(plan.id, elementId);
            } else {
                await deleteResourceFromCourse(plan.id, elementId);

                elements = elements.filter(e => e.id !== elementId);
            }
        }
    }

    // Posts the elelement activation to the API
    async function toggleElementActivation(eventDetails) {
        if (eventDetails) {
            const elementId = eventDetails.id;
            const activate = eventDetails.activate;
            const isFolder = eventDetails.isFolder;
            const changeActivationForFolderContent = eventDetails.changeActivationForFolderContent;

            const result = customActivateResourceFromPlan
                ? await customActivateResourceFromPlan(
                      elementId,
                      activate,
                      isFolder,
                      changeActivationForFolderContent
                  )
                : await activateResourceFromPlan(
                      elementId,
                      activate,
                      isFolder,
                      changeActivationForFolderContent
                  );

            if (result.success) {
                const newElements = [...elements];
                const targetId = newElements.findIndex(e => e.id === elementId);

                if (targetId >= 0) {
                    newElements[targetId].isActive = activate;
                    elements = newElements;
                }
            }
        }
    }

    function showMoveToModalChanged(eventDetails, element) {
        if (eventDetails && element) {
            showMoveToModal = false;
            showMoveToModal = eventDetails.showModal;
            elementMoveTo = element;
            sourcePlanTopicId = plan.topic ? plan.topic.id : noTopicTopicId;
        }
    }

    function elementMoved(eventDetails) {
        if (eventDetails) {
            const element = eventDetails.element;
            const targetPlanId = eventDetails.targetPlanId;

            // Update source plan elements
            elements = elements.filter(e => e.id !== element.id);

            // Update destination plan elements if needed (e.g. table view)
            dispatch("elementMovedToAnotherPlan", { element, targetPlanId });
        }
    }
</script>
